<script setup>
import { ref, watch, computed } from 'vue'
import RemoveResourceButton from '@/library/RemoveResourceButton.vue'
import ResourceDownloadButton from '@/resources/ResourceDownloadButton.vue'
import ResourcePreviewModal from '@/resources/ResourcePreviewModal.vue'
import IKImageS3Wrapper from '@/imagekit/IKImageS3Wrapper.vue'
import { getResourceSubjectLabelsFromValues } from '@/contentprofile/no_sel_composable'
import { storeToRefs } from 'pinia'
import { useVisibilityStore } from '@/visibility/visibility.store'
import { useUserStore } from '@/authentication/user.store'
import GbButton from '@/components/GbButton.vue'
import { actionPermissions, ACTION_TYPES, CONTENT_TYPES } from '@/util/access_visibility'
import SaveToFolderModal from '@/components/SaveToFolderModal.vue'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin.ts'
import { EVENT_NAMES } from '@/tracking/analytics/event_names.ts'
import CardContributor from '@/library/CardContributor.vue'
import { useContributorVisibility } from '@/library/composables/useContributorVisibility'
import DynamicHeading from '@/components/DynamicHeading.vue'

const props = defineProps({
  uuid: {
    required: true,
    type: String
  },
  title: {
    required: true,
    type: String
  },
  description: {
    required: true,
    type: String
  },
  type: {
    required: true,
    type: String
  },
  thumbnail_url: {
    required: true,
    type: String
  },
  grades: {
    required: true,
    type: Array
  },
  subjects: {
    required: true,
    type: Array
  },
  num_pages: {
    default: () => {
      return 0
    },
    type: Number
  },
  screenshots: {
    required: false,
    type: Array,
    default: () => {
      return []
    }
  },
  download_links: {
    required: true,
    type: Array
  },
  google_drive_links: {
    required: false,
    type: Array,
    default: () => {
      return []
    }
  },
  showThumbnail: {
    type: Boolean,
    default: true
  },
  language: {
    type: String,
    default: null
  },
  showResourceType: {
    type: Boolean,
    default: true
  },
  omitDescription: {
    type: Boolean,
    default: false
  },
  collectionUuid: {
    type: String,
    default: null
  },
  removable: {
    type: Boolean,
    default: true
  },
  shared: {
    type: Boolean,
    default: false
  },
  set_number: {
    type: Number,
    default: 0
  },
  headingLevel: {
    type: Number,
    default: 3
  }
})
const showPreviewModal = ref(false)
const showSaveModal = ref(false)
const resourceType = ref(CONTENT_TYPES.RESOURCE)
const analytics = getAnalyticsTracker()

const displaySubjects = ref(getResourceSubjectLabelsFromValues(props.subjects).filter(s => s !== ''))

const emit = defineEmits([
  'resourcePreviewShown',
  'resourceDownloadClicked',
  'resourceDownloadOpened',
  'resourceSaved'
])
const togglePreviewModal = (visible) => {
  showPreviewModal.value = visible
  if (visible) {
    emit('resourcePreviewShown')
  }
}
const toggleSaveModal = (visible) => {
  showSaveModal.value = visible
  if (visible) {
    analytics.sendEvent(EVENT_NAMES.RESOURCES.CLICKED_SAVE)
    emit('resourceSaved')
  }
}

const { selVisibility } = storeToRefs(useVisibilityStore())

watch(selVisibility, () => {
  displaySubjects.value = getResourceSubjectLabelsFromValues(props.subjects).filter(s => s !== '')
})

const { authInfo } = storeToRefs(useUserStore())

const savePermissions = computed(() => {
  return actionPermissions(ACTION_TYPES.SAVE, CONTENT_TYPES.RESOURCE, {}, authInfo.value)
})
const { contributorIsVisible } = useContributorVisibility(props.uuid)

const titleWithSet = computed(() => {
  if (props.set_number && props.set_number !== 0) {
    return `${props.title} - Set ${props.set_number}`
  }
  return props.title
})

const resolvedHeadingLevel = computed(() => {
  if (Number.isInteger(props.headingLevel) && props.headingLevel >= 1 && props.headingLevel <= 6) {
    return props.headingLevel
  }
  return 3
})
</script>
<template>
  <div class="flex flex-row px-5 pt-5">
    <div v-if="showThumbnail && screenshots.length > 0" class="flex flex-col p-5 text-center">
      <a
        tabindex="0"
        @click="togglePreviewModal(true)"
        @keyup.enter="togglePreviewModal(true)"
        class="grid hover:cursor-pointer [&_.invisible]:hover:!visible [&_.invisible]:hover:opacity-70"
        :title="`Preview ${title}`"
      >
        <div
          class="invisible z-30 col-span-full row-span-full flex h-full flex-col bg-black uppercase text-white"
        >
          <span class="material-icons-outlined pt-[50%]"> zoom_in </span>
          <span class="grow">Preview</span>
        </div>
        <div class="z-10 col-span-full row-span-full rotate-[-5deg] border border-gray-600" />
        <IKImageS3Wrapper
          :aws-url="thumbnail_url"
          alt=""
          :transformation="[{ width: 150 }]"
          class="z-20 col-span-full row-span-full border border-gray-600"
        />
      </a>
      <a
        tabindex="0"
        @click="togglePreviewModal(true)"
        @keyup.enter="togglePreviewModal(true)"
        role="button"
        :ariaLabel="`Preview ${title}`"
        class="pt-2 text-md text-blue-400"
      >
        Preview
      </a>
    </div>

    <div class="flex flex-col">
      <div class="leading-4">
        <span
          class="text-sm font-medium uppercase leading-5 text-gray-700"
          v-if="showResourceType"
          >{{ type }}</span
        >
        <span
          class="ml-2 rounded-xl bg-gray-800 px-2 py-0.5 text-sm text-white"
          v-if="language && language !== 'english'"
          >{{ $filters.capitalize(language) }}</span
        >
      </div>
      <DynamicHeading
        :headingLevel="resolvedHeadingLevel"
        :class="`${contributorIsVisible ? 'pb-1' : 'pb-2'} text-xl font-bold`"
        data-cy="resource-title"
      >
        {{ titleWithSet }}
      </DynamicHeading>
      <CardContributor v-if="!shared" :contentId="uuid"/>
      <p class="text-md" v-if="!omitDescription">{{ description }}</p>
      <slot name="resource-details">
        <div class="pt-2 text-md text-gray-700">
          <span v-if="grades.length > 0">
            Grade {{ grades.join(', ') }}
          </span>
          <span v-if="grades.length > 0 && displaySubjects.length > 0"> · </span>
          {{ displaySubjects.join(', ') }}
        </div>
      </slot>
      <div class="flex gap-2 pt-3">
        <ResourceDownloadButton
          :resource="{
            uuid,
            title,
            language,
            download_links,
            google_drive_links,
            set_number
          }"
          @resourceDownloadClicked="emit('resourceDownloadClicked')"
          @resourceDownloadOpened="emit('resourceDownloadOpened')"
        />
        <GbButton
          gbTheme="greenPrimary"
          v-if="!collectionUuid && !savePermissions.block"
          size="small"
          :aria-label="`Save ${title}`"
          @click="toggleSaveModal(true)"
          @keyup.enter="toggleSaveModal(true)"
        >
          <span class="material-icons-round pr-1 text-lg">save</span> Save
        </GbButton>
      </div>
      <ResourcePreviewModal
        v-if="showPreviewModal"
        @update:visible="togglePreviewModal"
        :uuid="uuid"
        :title="title"
        :type="type"
        :download-links="download_links"
        :google-drive-links="google_drive_links"
        :screenshots="screenshots"
        :num-pages="num_pages"
        :language="language"
      />
    </div>
    <RemoveResourceButton
      class="!relative !right-[unset] !top-[unset] min-w-8 !opacity-100"
      v-if="collectionUuid && removable"
      :resource="uuid"
      :collection="collectionUuid"
      :type="resourceType"
      :ariaLabel="`Remove ${title} from collection`"
    />
    <SaveToFolderModal
      v-if="!collectionUuid && showSaveModal"
      @update:visible="toggleSaveModal"
      :contentUuid="uuid"
      content-type="resource"
      content-type-label="Resource"
    />
  </div>
</template>
